import { Business, Person, Reservation, ReservationPaxGroup, CustomAttr, Service, ServiceBooking, ServiceBookingPaxGroup, ServiceOrder, ServiceOrderPaxGroup, ServiceSellPrice, ServiceSellCost, ServiceGroup, Payment } from '@turizi/shared';
import { applyDiscount, formatDay, formatTime } from '@/lib/format-utils';
import { MISSING_PRICE_BY } from '@turizi/shared';
import dayjs from 'dayjs';


export function ReservationToString(reservation: Reservation, t?: (key: string) => string): string {
    if (!reservation) return '';
    const bookingsStr = reservation.service_bookings?.map(b => ServiceBookingToString(b, false)).join(' | ') || '';
    const personsStr = reservation.pax_groups?.flatMap(g => [g.name, ...(g.persons || []).map(p => p.name)]).filter(Boolean).join(' | ') || '';

    return `
    #${reservation.id} |
    ${reservation.status} |
    ${personsStr} |
    ${reservation.observations} |
    ${reservation.client_business?.name} |
    ${formatDay(reservation.start_day)} |
    ${formatDay(reservation.end_day)} |
    ${bookingsStr}
    `.toLocaleLowerCase();
}

export const ServiceToString = (service: Service, t: (key: string) => string) => {
    const providers = service.providers ? Object.values(service.providers) : [];
    const providerNames = providers.map(provider => provider.name).filter(Boolean).join(' | ');
    const cities = providers.map(provider => provider.city).filter(Boolean).join(' | ');
    const category = t(service?.category);
    const providerCategories = providers.map(provider => t(provider.category)).filter(Boolean).join(' | ');
    const providerAttrs = providers.flatMap(provider =>
        provider.self_attrs?.map(attr => t(attr.key) + ': ' + t(attr.value)) || []
    ).join(' | ');

    return `
    #${service.id} |
    ${service.name} |
    ${providerNames} |
    ${category} |
    ${service.city} |
    ${cities} |
    ${providerCategories} |
    ${providerAttrs}
    ${service.price_by || MISSING_PRICE_BY} |
    ${service.cost_by || MISSING_PRICE_BY} |
    ${service.self_attrs?.map(attr => t(attr.key) + ': ' + t(attr.value)).join(' | ') || ''}
    ${service.booking_attrs?.map(attr => t(attr.key) + ': ' + t(attr.value)).join(' | ') || ''}
    `.toLowerCase();
}

export function PersonToString(person: Person, t: (key: string) => string): string {
    const aux = `
        #${person.id} |
        ${person.name} |
        ${person.observations} |
        ${t(person.category)} |
        ${person.email} |
        ${person.phone} |
        ${person.address} |
        ${person.document_type} |
        ${person.document_number} |
        ${person.tags.map(tag => t(tag).toLowerCase()).join(' | ')}
    `.toLocaleLowerCase();
    return aux;
}

export function BusinessToString(business: Business, t: (key: string) => string): string {
    if (!business) return '';
    const aux = `
        #${business.id} |
        ${business.name || ''} |
        ${JSON.stringify(business.emails || {})} |
        ${JSON.stringify(business.phones || {})} |
        ${business.country_code || ''} |
        ${business.city || ''} |
        ${business.address || ''} |
        ${business.observations || ''} |
        ${business.self_attrs?.map(attr => attr.key + ': ' + attr.value).join(' | ') || ''} |
        ${t(`${business.category || ''}`)}
    `.toLocaleLowerCase();
    return aux;
}

export const ServiceBookingToString = (serviceBooking: ServiceBooking, includeReservation = true) => {
    if (!serviceBooking) return '';
    const providers = Object.values(serviceBooking.service_data?.providers || {}).map(provider => provider.name);
    const bookingPersonsStr = serviceBooking.pax_groups?.flatMap(pg => [
        pg.pax_group?.name || '',
        ...(pg.pax_group?.persons || []).map(p => p.name)
    ]).filter(Boolean).join(' | ') || '';

    return `
    #${serviceBooking.id} |
    ${serviceBooking.service_data?.name} |
    ${providers.join(' | ')}
    ${serviceBooking.service_data?.category} |
    ${serviceBooking?.self_attrs?.map(attr => attr.key + ': ' + attr.value).join(' | ') || ''}
    ${serviceBooking.service_data?.self_attrs?.map(attr => attr.key + ': ' + attr.value).join(' | ') || ''}
    ${serviceBooking.service_data?.booking_attrs?.map(attr => attr.key + ': ' + attr.value).join(' | ') || ''}
    ${bookingPersonsStr} |
    ${includeReservation && serviceBooking.reservation ? ReservationToString(serviceBooking.reservation, () => '') : ''}
    adults: ${serviceBooking.adults || 0} | children: ${serviceBooking.children || 0} | seniors: ${serviceBooking.seniors || 0} | infants: ${serviceBooking.infants || 0}
    `.toLowerCase();
}

export const ServiceOrderToString = (serviceOrder: ServiceOrder) => {
    if (!serviceOrder) return '';
    return `
    #${serviceOrder.id} |
    ${serviceOrder.service_order_bookings?.map(booking => ServiceBookingToString(booking?.service_booking)).join(' | ') || ''}
    `.toLowerCase();
}

export function getServiceSellPrice(service: Service, start_day: string, end_day?: string,): ServiceSellPrice[] | null {

    // Filter valid prices (with default_amount)
    const validPrices = service?.sell_prices?.filter(price =>
        price.default_amount !== null && price.default_amount !== undefined
    );

    const check_start = start_day!;
    const check_end = end_day || start_day!;

    const activePrices = validPrices?.filter(price => {
        if (!price.start_day && !price.end_day) {
            return true;
        }

        if (price.start_day && price.end_day) {
            return price.start_day <= check_start && price.end_day >= check_end;
        }

        if (price.start_day && !price.end_day) {
            return price.start_day <= check_start;
        }

        if (!price.start_day && price.end_day) {
            return price.end_day >= check_end;
        }

        return false;
    });

    const sortedPrices = activePrices?.sort((a, b) => {
        if (!a.start_day && !b.start_day) return 0;
        if (!a.start_day) return 1;
        if (!b.start_day) return -1;
        return a.start_day < b.start_day ? 1 : -1;
    });

    return sortedPrices;
}

export function getServiceSellCost(service: Service, start_day?: string, end_day?: string, providerId?: string): ServiceSellCost | null {
    if (!service?.sell_costs || service.sell_costs.length === 0) {
        return null;
    }

    // Filter valid costs (with default_amount and matching providerId if specified)
    const validCosts = service.sell_costs.filter(cost => {
        if (providerId && Number(cost.business_id) !== Number(providerId)) {
            return false;
        }
        return cost.default_amount !== null && cost.default_amount !== undefined;
    });



    if (validCosts.length === 0) {
        return null;
    }

    // If no date provided, return the latest cost (most recent start_day)
    if (!start_day) {
        const sortedByDate = [...validCosts].sort((a, b) => {
            if (!a.start_day && !b.start_day) return 0;
            if (!a.start_day) return 1;
            if (!b.start_day) return -1;
            return b.start_day.localeCompare(a.start_day);
        });
        return sortedByDate[0];
    }

    const check_start = start_day!;
    const check_end = end_day || start_day!;

    // Find costs that are active for the given date range
    const activeCosts = validCosts.filter(cost => {
        if (!cost.start_day && !cost.end_day) {
            return true;
        }

        if (cost.start_day && cost.end_day) {
            return cost.start_day <= check_start && cost.end_day >= check_end;
        }

        if (cost.start_day && !cost.end_day) {
            return cost.start_day <= check_start;
        }

        if (!cost.start_day && cost.end_day) {
            return cost.end_day >= check_end;
        }

        return false;
    });

    // Sort by specificity and recency
    activeCosts.sort((a, b) => {
        // First, prioritize costs with both dates defined
        const aSpecificity = (a.start_day ? 1 : 0) + (a.end_day ? 1 : 0);
        const bSpecificity = (b.start_day ? 1 : 0) + (b.end_day ? 1 : 0);

        if (aSpecificity !== bSpecificity) {
            return bSpecificity - aSpecificity; // Higher specificity first
        }

        // Then sort by start date (more recent first)
        if (a.start_day && b.start_day) {
            return b.start_day.localeCompare(a.start_day);
        }

        // If one has start_day and other doesn't, prefer the one with start_day
        if (a.start_day && !b.start_day) return -1;
        if (!a.start_day && b.start_day) return 1;

        return 0;
    });

    // Return the most relevant cost
    return activeCosts[0];
}

export function businessChanged(business: Business, newBusiness: Business): boolean {
    return business?.name !== newBusiness?.name ||
        business?.address !== newBusiness?.address ||
        business?.city !== newBusiness?.city ||
        business?.country_code !== newBusiness?.country_code ||
        business?.observations !== newBusiness?.observations ||
        business?.category !== newBusiness?.category ||
        JSON.stringify(business?.self_attrs) !== JSON.stringify(newBusiness?.self_attrs);
}

export const hasServiceChanged = (service: Service | undefined, newService: Service | undefined) => {
    if (!service || !newService) return true;

    const oldProviderIds = service.providers ? Object.keys(service.providers).sort() : [];
    const newProviderIds = newService.providers ? Object.keys(newService.providers).sort() : [];

    return (
        service?.id !== newService?.id ||
        service?.name !== newService?.name ||
        service?.category !== newService?.category ||
        service?.observations !== newService?.observations ||
        JSON.stringify(oldProviderIds) !== JSON.stringify(newProviderIds) ||
        JSON.stringify(service?.self_attrs) !== JSON.stringify(newService?.self_attrs) ||
        JSON.stringify(service?.booking_attrs) !== JSON.stringify(newService?.booking_attrs) ||
        // JSON.stringify(service?.prices) !== JSON.stringify(newService?.prices) ||
        JSON.stringify(service?.discounts) !== JSON.stringify(newService?.discounts)
    );
}

export function selfAttrsKeysFromServices(services: Service[]): string[] {
    const keys = services.map(service => [
        ...(service.self_attrs || []),
        ...(service.providers ? Object.values(service.providers).flatMap(provider => provider.self_attrs || []) : []),
    ]?.map(attr => attr.key))
        .flat()
        .filter((key, index, self) => self.indexOf(key) === index)
        .sort();
    return keys;
}

export function selfAttrsKeyValue(services: Service[]): Record<string, Set<string>> {
    const result: Record<string, Set<string>> = {};
    services.map(service => {
        [
            ...(service.self_attrs || []),
            ...(service.providers ? Object.values(service.providers).flatMap(provider => provider.self_attrs || []) : []),
        ].map(attr => {
            if (
                (attr.value?.toString() || '').length > 55 ||
                (attr.value?.toString() || '').length === 0
            ) {
                return;
            }
            if (!result[attr.key]) {
                result[attr.key] = new Set();
            }
            result[attr.key].add(attr.value?.toString() || '');
        });
    });
    return result;
}

export function updateAttrValue(attrs: CustomAttr[], key: string, value: any): CustomAttr[] {
    if (!attrs.find(attr => attr.key === key)) { // if the attribute doesn't exist, add it
        return [...attrs, { key, value, type: 'text', }];
    }
    return attrs.map(attr => attr.key === key ? { ...attr, value } : attr); // if the attribute exists, update the value
}

export function parseAttrValue(value: string | undefined | null, type: string): any {
    if (value === null || value === undefined || value === '') return undefined;
    if (type === 'number') return Number(value);
    if (type === 'time') return formatTime(value, undefined);
    // TODO: boolean values are treated as strings in dict.
    return value;
}

export function getAttrValue(item: Service | ServiceBooking | Business | Person, key: string): any {
    const attr = item?.self_attrs?.find(attr => attr.key === key);
    return parseAttrValue(attr?.value, attr?.type);
}

export const getAllAttrsFromService = (service: Service): CustomAttr[] => {
    const providerAttrs = service.providers ?
        Object.values(service.providers).flatMap(provider => provider.self_attrs || []) : [];

    return [
        ...service.self_attrs,
        ...providerAttrs,
        ...service.booking_attrs,
    ];
}

export const getMergedAttrsFromService = (service: Service, customAttrs: CustomAttr[]): CustomAttr[] => {
    const providerAttrs = service.providers ?
        Object.values(service.providers).flatMap(provider => provider.self_attrs || []) : [];

    return [
        ...service.self_attrs,
        ...providerAttrs,
        ...service.booking_attrs,
        ...customAttrs
    ];
}

export function sumByKey(list: any[], key: string): Record<string, number> {
    return list.reduce((acc, item) => {
        if (item[key]) {
            acc[item[key]] = (acc[item[key]] || 0) + item[key];
        }
        return acc;
    }, {} as Record<string, number>);
}

export function isRangeService(category: string): boolean {
    switch (category) {
        case 'stay':
            return true;
        case 'rental':
            return true;
        case 'car_rental':
            return true;
        default:
            return false;
    }
}

export function getServiceGroupTotalPrice(serviceGroup: ServiceGroup): { amount: number, currency: string } {
    const today = dayjs().format('YYYY-MM-DD');
    const serviceLinks = serviceGroup.service_links || [];

    const totalPrice = serviceLinks.reduce((acc, link) => {
        const activePrice = getServiceSellPrice(link.service, today)?.[0];
        const price = activePrice ? activePrice.default_amount : 0;
        const discountPercentage = link.discount || 0;
        const finalPrice = applyDiscount(price, discountPercentage);

        return acc + finalPrice;
    }, 0);

    const currency = serviceLinks[0]?.service.sell_prices?.[0]?.default_currency || '---';

    return { amount: totalPrice, currency };
}

export function getServiceGroupBasePrice(serviceGroup: ServiceGroup): { amount: number, currency: string } {
    const today = dayjs().format('YYYY-MM-DD');
    const serviceLinks = serviceGroup.service_links || [];

    const basePrice = serviceLinks.reduce((acc, link) => {
        const activePrice = getServiceSellPrice(link.service, today)?.[0];
        const price = activePrice ? activePrice.default_amount : 0;

        return acc + price;
    }, 0);

    const currency = serviceLinks[0]?.service.sell_prices?.[0]?.default_currency || '---';

    return { amount: basePrice, currency };
}

/**
 * Calculates the total occupied seat slots on a Service Order.
 *
 * Algorithm Overview:
 * 1. Obtains the passenger breakdown per category via `calculateUniquePaxCategories(order)`.
 * 2. Sums seat-occupying passenger categories: `adults` + `children` + `seniors` + `guests`.
 *    (Note: Infants do NOT occupy seats/space and are explicitly excluded from seat occupancy).
 * 3. Scans linked bookings (`service_order_bookings`) and direct bookings (`service_bookings`)
 *    for any booking that has 0 seat passengers specified but defines explicit `inventory_slots`,
 *    adding those extra inventory slots to the seat count.
 * 4. Returns `seatPaxs + extraInventorySlots`.
 *
 * @param order The Service Order object to analyze
 * @returns Total occupied seat count
 */
export function calculateOccupiedSlots(order: ServiceOrder): number {
    if (!order) return 0;

    const uniquePaxs = calculateUniquePaxCategories(order);
    // Infants DO NOT occupy space / seats!
    const seatPaxs = (uniquePaxs.adults || 0) +
        (uniquePaxs.children || 0) +
        (uniquePaxs.seniors || 0) +
        (uniquePaxs.guests || 0);

    let extraInventorySlots = 0;
    const processedBookingIds = new Set<number>();

    // Check linked bookings for 0-pax bookings with specified inventory_slots
    (order.service_order_bookings || []).forEach((link) => {
        const b = link.service_booking;
        if (b && b.id) {
            processedBookingIds.add(b.id);
        }
        const linkPaxsSum = (link.adults || 0) + (link.children || 0) + (link.seniors || 0) + (link.guests || 0);
        const bookingPaxsSum = b ? ((b.adults || 0) + (b.children || 0) + (b.seniors || 0) + (b.guests || 0)) : 0;

        if (linkPaxsSum === 0 && bookingPaxsSum === 0 && b && b.inventory_slots) {
            extraInventorySlots += b.inventory_slots;
        }
    });

    // Check direct bookings for 0-pax bookings with specified inventory_slots
    (order.service_bookings || []).forEach((b) => {
        if (!b || (b.id && processedBookingIds.has(b.id))) return;
        const bookingPaxsSum = (b.adults || 0) + (b.children || 0) + (b.seniors || 0) + (b.guests || 0);

        if (bookingPaxsSum === 0 && b.inventory_slots) {
            extraInventorySlots += b.inventory_slots;
        }
    });

    return seatPaxs + extraInventorySlots;
}

/**
 * Resolves the effective list of passengers for a `ServiceBookingPaxGroup`.
 * - When the booking group has explicit `booking_persons` (`is_custom_config` or `booking_persons.length > 0`),
 *   returns those specific passengers with their booking-level category counts.
 * - Otherwise, returns the full `pax_group.persons` list from the reservation group.
 */
export function resolveBookingPaxGroupPersons(
    bg?: ServiceBookingPaxGroup | null,
    fallbackGroup?: ReservationPaxGroup | null
): Person[] {
    const pg = bg?.pax_group || fallbackGroup;
    const hasExplicitBookingPersons = Boolean(
        bg && Array.isArray(bg.booking_persons) && (bg.booking_persons.length > 0 || bg.is_custom_config)
    );

    if (hasExplicitBookingPersons) {
        return (bg!.booking_persons || [])
            .map((bp) => {
                const basePerson = bp.person || bp.person_data || pg?.persons?.find((gp) => gp.id === bp.person_id);
                if (!basePerson) return null;
                return {
                    ...basePerson,
                    adults: bp.adults ?? basePerson.adults ?? 0,
                    children: bp.children ?? basePerson.children ?? 0,
                    seniors: bp.seniors ?? basePerson.seniors ?? 0,
                    infants: bp.infants ?? basePerson.infants ?? 0,
                    guests: bp.guests ?? basePerson.guests ?? 0,
                } as Person;
            })
            .filter((p): p is Person => p !== null);
    }

    return pg?.persons || [];
}

/**
 * Resolves the `ServiceOrderPaxGroup[]` assigned to a booking on a Service Order,
 * normalizing each group's `pax_group.persons` to reflect the booking's effective passengers.
 */
export function resolveOrderBookingPaxGroups(
    order: ServiceOrder,
    booking: ServiceBooking
): ServiceOrderPaxGroup[] {
    return (order.service_order_pax_groups || [])
        .filter((sog) => sog.service_booking_id === booking.id)
        .map((sog) => {
            const match = booking.pax_groups?.find(
                (bg) => (bg.pax_group_id || bg.pax_group?.id || bg.id) === sog.pax_group_id
            );
            const pg = sog.pax_group || match?.pax_group;
            const resolvedPersons = resolveBookingPaxGroupPersons(match, pg);

            return {
                ...sog,
                pax_group: pg ? { ...pg, persons: resolvedPersons } : pg,
            };
        });
}

/**
 * Calculates the total passenger counts per category (adults, children, seniors, infants, guests)
 * for a Service Order.
 *
 * Algorithm Overview (Option A):
 * 1. Reads the allocated passenger counts directly from the Service Order link (`link.adults`, `link.children`, etc.) when present.
 * 2. Builds a deduplication key per booking from its assigned `pax_group_id`s (including sorted `booking_persons` IDs when `is_custom_config` is true).
 * 3. Takes `Math.max()` per category for multi-leg bookings sharing the exact same group key on the order.
 * 4. Sums the max category totals across distinct group keys and unassigned bookings.
 *
 * @param order The Service Order object to analyze
 * @returns Object with category totals: `{ adults, children, seniors, infants, guests }`
 */
export function calculateUniquePaxCategories(order: ServiceOrder) {
    const totals = {
        adults: 0,
        children: 0,
        seniors: 0,
        infants: 0,
        guests: 0,
    };

    if (!order) return totals;

    const bookingGroups = new Map<string, Array<{ adults: number; children: number; seniors: number; infants: number; guests: number }>>();
    const processedBookingIds = new Set<number>();

    // Helper to build a deduplication key for a booking based on its pax_group_id (+ custom booking_persons IDs)
    const getBookingDedupeKey = (b: ServiceBooking | undefined, fallbackId: number | null): string => {
        if (!b) return `booking-${fallbackId ?? Math.random()}`;

        const assignedOrderGroups = (order.service_order_pax_groups || [])
            .filter((sog) => sog.service_booking_id === b.id);

        const groupIds = assignedOrderGroups.length > 0
            ? assignedOrderGroups.map((sog) => sog.pax_group_id).filter((id): id is number => typeof id === 'number')
            : (b.pax_groups || []).map((bg) => bg.pax_group_id || bg.pax_group?.id || bg.id).filter((id): id is number => typeof id === 'number');

        if (groupIds.length === 0) {
            return `booking-${b.id ?? fallbackId ?? Math.random()}`;
        }

        const groupTokens = Array.from(new Set(groupIds))
            .sort((a, bId) => a - bId)
            .map((gid) => {
                const bg = (b.pax_groups || []).find(
                    (pg) => (pg.pax_group_id || pg.pax_group?.id || pg.id) === gid
                );
                const hasCustomPersons = Boolean(
                    bg && bg.is_custom_config && Array.isArray(bg.booking_persons) && bg.booking_persons.length > 0
                );
                if (hasCustomPersons) {
                    const customPids = Array.from(
                        new Set(
                            (bg!.booking_persons || [])
                                .map((bp) => bp.person_id ?? bp.person?.id ?? bp.person_data?.id)
                                .filter((id): id is number => typeof id === 'number')
                        )
                    ).sort((a, bPid) => a - bPid);

                    if (customPids.length > 0) {
                        return `group-${gid}-paxs-${customPids.join(',')}`;
                    }
                }
                return `group-${gid}`;
            });

        return groupTokens.join('|');
    };

    const addBookingPaxData = (
        key: string,
        paxData: { adults: number; children: number; seniors: number; infants: number; guests: number }
    ) => {
        if (!bookingGroups.has(key)) {
            bookingGroups.set(key, []);
        }
        bookingGroups.get(key)!.push(paxData);
    };

    // 1. Process linked bookings (service_order_bookings)
    (order.service_order_bookings || []).forEach((link) => {
        const b = link.service_booking;
        if (b && b.id) {
            processedBookingIds.add(b.id);
        }

        const hasExplicitLinkAllocation =
            (link.adults !== undefined && link.adults !== null) ||
            (link.children !== undefined && link.children !== null) ||
            (link.seniors !== undefined && link.seniors !== null) ||
            (link.guests !== undefined && link.guests !== null) ||
            (link.infants !== undefined && link.infants !== null);

        const paxData = hasExplicitLinkAllocation ? {
            adults: link.adults || 0,
            children: link.children || 0,
            seniors: link.seniors || 0,
            infants: link.infants || 0,
            guests: link.guests || 0,
        } : {
            adults: b?.adults || 0,
            children: b?.children || 0,
            seniors: b?.seniors || 0,
            infants: b?.infants || 0,
            guests: b?.guests || 0,
        };

        const key = getBookingDedupeKey(b, link.id || link.service_booking_id || null);
        addBookingPaxData(key, paxData);
    });

    // 2. Process direct bookings (service_bookings)
    (order.service_bookings || []).forEach((b) => {
        if (!b || (b.id && processedBookingIds.has(b.id))) return;
        if (b.id) processedBookingIds.add(b.id);

        const paxData = {
            adults: b.adults || 0,
            children: b.children || 0,
            seniors: b.seniors || 0,
            infants: b.infants || 0,
            guests: b.guests || 0,
        };

        const key = getBookingDedupeKey(b, b.id);
        addBookingPaxData(key, paxData);
    });

    // For each group key, take Math.max per category across multi-leg bookings
    for (const groupBookings of bookingGroups.values()) {
        let maxAdults = 0;
        let maxChildren = 0;
        let maxSeniors = 0;
        let maxInfants = 0;
        let maxGuests = 0;

        for (const b of groupBookings) {
            maxAdults = Math.max(maxAdults, b.adults);
            maxChildren = Math.max(maxChildren, b.children);
            maxSeniors = Math.max(maxSeniors, b.seniors);
            maxInfants = Math.max(maxInfants, b.infants);
            maxGuests = Math.max(maxGuests, b.guests);
        }

        totals.adults += maxAdults;
        totals.children += maxChildren;
        totals.seniors += maxSeniors;
        totals.infants += maxInfants;
        totals.guests += maxGuests;
    }

    return totals;
}

/**
 * Resolves the applied payment amount for a specific reservation from a Payment instance.
 */
export function getPaymentAppliedAmount(payment: Payment, reservationId?: number | null): number {
    if (!payment) return 0;
    const resId = reservationId ? Number(reservationId) : null;

    // 1. Direct junction on payment item
    const directLink = payment.PaymentReservation || payment.payment_reservation;
    if (directLink && directLink.amount !== undefined && directLink.amount !== null) {
        if (!resId || !directLink.reservation_id || Number(directLink.reservation_id) === resId) {
            return Number(directLink.amount);
        }
    }

    // 2. Included reservations array (from Sequelize eager loading)
    if (resId && payment.reservations && payment.reservations.length > 0) {
        const resMatch = payment.reservations.find(r => Number(r.id) === resId);
        if (resMatch) {
            const link = resMatch.PaymentReservation || resMatch.payment_reservation;
            if (link && link.amount !== undefined && link.amount !== null) {
                return Number(link.amount);
            }
            if (payment.reservations.length === 1 && payment.amount) {
                return Number(payment.amount);
            }
        }
    }

    // 3. Raw payload affected_reservations array
    if (resId && Array.isArray(payment.affected_reservations)) {
        const aff = payment.affected_reservations.find((item: any) =>
            typeof item === 'object' && item !== null && Number(item.id || item.reservation_id) === resId
        );
        if (aff && typeof aff === 'object' && aff.amount !== undefined && aff.amount !== null) {
            return Number(aff.amount);
        }
    }

    // 4. Default to payment amount if single reservation or unassigned
    if (!resId || (payment.reservations && payment.reservations.length <= 1)) {
        return Number(payment.amount || 0);
    }

    return 0;
}

/**
 * Resolves the applied payment amount for a specific service booking from a Payment instance.
 */
export function getPaymentServiceBookingAppliedAmount(payment: Payment, bookingId?: number | null): number {
    if (!payment) return 0;
    const bkId = bookingId ? Number(bookingId) : null;

    const directLink = payment.PaymentServiceBooking || payment.payment_service_booking;
    if (directLink && directLink.amount !== undefined && directLink.amount !== null) {
        if (!bkId || !directLink.service_booking_id || Number(directLink.service_booking_id) === bkId) {
            return Number(directLink.amount);
        }
    }

    if (bkId && payment.service_bookings && payment.service_bookings.length > 0) {
        const bkMatch = payment.service_bookings.find(b => Number(b.id) === bkId);
        if (bkMatch) {
            const link = bkMatch.PaymentServiceBooking || bkMatch.payment_service_booking;
            if (link && link.amount !== undefined && link.amount !== null) {
                return Number(link.amount);
            }
            if (payment.service_bookings.length === 1 && payment.amount) {
                return Number(payment.amount);
            }
        }
    }

    if (bkId && Array.isArray(payment.affected_service_bookings)) {
        const aff = payment.affected_service_bookings.find((item: any) =>
            typeof item === 'object' && item !== null && Number(item.id || item.service_booking_id) === bkId
        );
        if (aff && typeof aff === 'object' && aff.amount !== undefined && aff.amount !== null) {
            return Number(aff.amount);
        }
    }

    if (!bkId || (payment.service_bookings && payment.service_bookings.length <= 1)) {
        return Number(payment.amount || 0);
    }

    return 0;
}
